import React, { useState, useEffect, useCallback } from "react";
import { SkeletonCard } from "@auction-eth/ui-kit";
import type { Case } from "@auction-eth/shared-types";
import { api } from "../services/api.js";

type PipelineStage =
  | "INVITE_RETRIEVE"
  | "AWAIT_DAA1_CONFIRM"
  | "PROCESSING"
  | "INSPECTION_SCHEDULED"
  | "SOLD_HANDOVER"
  | "TRANSFERRED";

interface PipelineLane {
  id: PipelineStage;
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  flagStart: string;
  flagComplete: string;
}

const LANES: PipelineLane[] = [
  {
    id: "INVITE_RETRIEVE",
    label: "Invite / Retrieve",
    color: "#d97706",
    bgColor: "#fff4db",
    borderColor: "#f59e0b",
    flagStart: "hub_dispatch_started",
    flagComplete: "hub_dispatch_completed",
  },
  {
    id: "AWAIT_DAA1_CONFIRM",
    label: "Await DAA1 Witness",
    color: "#7c3aed",
    bgColor: "#f3e8ff",
    borderColor: "#8b5cf6",
    flagStart: "hub_witness_started",
    flagComplete: "hub_witness_completed",
  },
  {
    id: "PROCESSING",
    label: "Processing",
    color: "#0ea5e9",
    bgColor: "#e0f2fe",
    borderColor: "#38bdf8",
    flagStart: "hub_processing_started",
    flagComplete: "hub_processing_completed",
  },
  {
    id: "INSPECTION_SCHEDULED",
    label: "Inspection",
    color: "#1d4ed8",
    bgColor: "#e8f0ff",
    borderColor: "#3b82f6",
    flagStart: "hub_inspection_started",
    flagComplete: "hub_inspection_completed",
  },
  {
    id: "SOLD_HANDOVER",
    label: "Sold / Handover",
    color: "#15803d",
    bgColor: "#e9f8ee",
    borderColor: "#22c55e",
    flagStart: "hub_release_started",
    flagComplete: "hub_release_completed",
  },
  {
    id: "TRANSFERRED",
    label: "Transferred",
    color: "#475569",
    bgColor: "#edf2f7",
    borderColor: "#94a3b8",
    flagStart: "settlement_started",
    flagComplete: "settlement_completed",
  },
];

function deriveHubStage(hubFlags: Record<string, boolean | null>): PipelineStage {
  if (hubFlags["hub_release_completed"] === true) return "TRANSFERRED";
  if (hubFlags["hub_release_started"] === true) return "SOLD_HANDOVER";
  if (hubFlags["hub_inspection_started"] === true) return "INSPECTION_SCHEDULED";
  if (hubFlags["hub_processing_started"] === true) return "PROCESSING";
  if (hubFlags["hub_witness_started"] === true) return "AWAIT_DAA1_CONFIRM";
  if (hubFlags["hub_dispatch_started"] === true) return "INVITE_RETRIEVE";
  return "INVITE_RETRIEVE";
}

interface CasePipelineCardProps {
  caseData: Case;
  stage: PipelineStage;
  lane: PipelineLane;
}

function CasePipelineCard({ caseData, lane }: CasePipelineCardProps): React.ReactElement {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid var(--border)`,
        borderLeft: `4px solid ${lane.borderColor}`,
        borderRadius: "var(--radius-sm)",
        padding: "12px 14px",
        marginBottom: 8,
        transition: "box-shadow 0.16s ease",
        cursor: "default",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = "0 4px 16px rgba(15,23,42,0.10)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = "";
      }}
    >
      <p
        style={{
          margin: "0 0 4px",
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "var(--muted)",
        }}
      >
        {caseData.case_id}
      </p>
      <p style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 700, color: "var(--ink)" }}>
        {caseData.borrower_id ?? "Unknown Borrower"}
      </p>
      <p style={{ margin: 0, fontSize: 12, color: "var(--muted)" }}>
        {caseData.linked_loan_id ? `Loan: ${caseData.linked_loan_id}` : caseData.lender_id}
      </p>
    </div>
  );
}

export function HubOpsPipelinePage(): React.ReactElement {
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    void api.cases.list({ is_closed: false }).then((res) => {
      setCases(res.data);
      setLoading(false);
    }).catch((err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to load cases");
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30_000);
    return () => clearInterval(interval);
  }, [load]);

  // Categorise cases into pipeline lanes
  const laneMap = new Map<PipelineStage, Array<{ caseData: Case; lane: PipelineLane }>>();
  LANES.forEach((lane) => laneMap.set(lane.id, []));

  cases.forEach((c) => {
    const allFlags = c.flags as unknown as Record<string, Record<string, boolean | null>>;
    const hubFlags = allFlags["hub_ops"] ?? {};
    // Only show cases that have entered hub_ops workflow
    if (!hubFlags["hub_dispatch_started"]) return;
    const stage = deriveHubStage(hubFlags);
    const lane = LANES.find((l) => l.id === stage);
    if (lane) {
      laneMap.get(stage)!.push({ caseData: c, lane });
    }
  });

  const totalHubCases = Array.from(laneMap.values()).reduce((sum, arr) => sum + arr.length, 0);

  return (
    <>
      <div style={{ marginBottom: 28 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 24,
            fontWeight: 900,
            color: "var(--ink)",
          }}
        >
          Hub Ops Pipeline
        </h1>
        <p style={{ margin: "4px 0 0", color: "var(--muted)", fontSize: 14 }}>
          {totalHubCases} cases in hub workflow · refreshes every 30s
        </p>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            background: "var(--danger-bg)",
            color: "var(--danger)",
            borderRadius: "var(--radius-sm)",
            padding: "12px 16px",
            marginBottom: 20,
            fontSize: 14,
          }}
        >
          {error}{" "}
          <button
            onClick={load}
            style={{ background: "none", border: "none", color: "var(--brand)", cursor: "pointer", fontWeight: 700 }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Kanban board */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${LANES.length}, minmax(180px, 1fr))`,
          gap: 12,
          overflowX: "auto",
          paddingBottom: 16,
        }}
      >
        {LANES.map((lane) => {
          const cards = laneMap.get(lane.id) ?? [];
          return (
            <div key={lane.id}>
              {/* Lane header */}
              <div
                style={{
                  background: lane.bgColor,
                  border: `1px solid ${lane.borderColor}`,
                  borderRadius: "var(--radius-sm)",
                  padding: "8px 12px",
                  marginBottom: 10,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: lane.color,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  {lane.label}
                </span>
                <span
                  style={{
                    background: lane.color,
                    color: "#fff",
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "2px 8px",
                    minWidth: 20,
                    textAlign: "center",
                  }}
                >
                  {cards.length}
                </span>
              </div>

              {/* Lane cards */}
              <div style={{ minHeight: 80 }}>
                {loading ? (
                  <SkeletonCard />
                ) : cards.length === 0 ? (
                  <div
                    style={{
                      border: "2px dashed var(--border)",
                      borderRadius: "var(--radius-sm)",
                      height: 60,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "var(--muted)",
                      fontSize: 12,
                    }}
                  >
                    No cases
                  </div>
                ) : (
                  cards.map(({ caseData, lane: cardLane }) => (
                    <CasePipelineCard
                      key={caseData.case_id}
                      caseData={caseData}
                      stage={lane.id}
                      lane={cardLane}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div
        style={{
          marginTop: 32,
          background: "var(--panel)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-sm)",
          padding: "16px 20px",
        }}
      >
        <p
          style={{
            margin: "0 0 12px",
            fontSize: 12,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "var(--muted)",
          }}
        >
          Pipeline Stages
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 20px" }}>
          {LANES.map((lane) => (
            <div key={lane.id} style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: lane.color,
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: 12, color: "var(--slate)" }}>{lane.label}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
